
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UserProfile, NotificationPreferences } from "@/types/profile";
import { ArrowLeft, ArrowRight, CheckCircle2, Shield } from "lucide-react";
import { Json } from "@/integrations/supabase/types";

// Setup wizard steps
enum SetupStep {
  WELCOME = 0,
  BASIC_INFO = 1,
  ID_VERIFICATION = 2,
  PROFILE_PIC = 3,
  ROLE_DETAILS = 4,
  FINAL = 5
}

const AccountSetupWizard = () => {
  const [currentStep, setCurrentStep] = useState<SetupStep>(SetupStep.WELCOME);
  const [profile, setProfile] = useState<Partial<UserProfile> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch current user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          // Not authenticated, redirect to auth page
          toast({
            title: "Authentication required",
            description: "Please sign in to complete your profile setup",
            variant: "destructive",
          });
          navigate("/auth");
          return;
        }

        // Get user profile from database
        const { data: profileData, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error fetching profile:", error);
          toast({
            title: "Error",
            description: "Could not load your profile data. Please try again.",
            variant: "destructive",
          });
          return;
        }

        // If profile setup is already complete, redirect to dashboard
        if (profileData && profileData.onboarding_completed) {
          navigate("/dashboard");
          return;
        }

        // Transform Supabase data to match our UserProfile type
        const transformedProfile: Partial<UserProfile> = {
          ...profileData,
          notification_preferences: profileData.notification_preferences as unknown as NotificationPreferences,
          social_links: profileData.social_links as any || {},
          streaming_links: profileData.streaming_links as any || {},
          role_types: profileData.role_types as any || [],
          privacy_settings: profileData.privacy_settings as any || {},
          rate_card: profileData.rate_card as any || {},
          availability: profileData.availability as any || {},
          payment_details: profileData.payment_details as any || {},
          testimonials: profileData.testimonials as any || []
        };

        setProfile(transformedProfile);
        
        // Calculate initial progress
        calculateProgress(profileData);
      } catch (error) {
        console.error("Setup wizard error:", error);
        toast({
          title: "Error",
          description: "Something went wrong. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProgress();
    fetchProfile();
  }, [navigate, toast]);

  // Calculate and update progress based on profile data
  const calculateProgress = (profileData: any) => {
    let completedSteps = 0;
    const totalSteps = 6; // Total number of steps in our wizard

    if (profileData) {
      // Count completed steps
      if (profileData.first_name && profileData.last_name) completedSteps++;
      if (profileData.identity_verified) completedSteps++;
      if (profileData.profile_pic) completedSteps++;
      if (profileData.bio) completedSteps++;
      if (profileData.location) completedSteps++;

      // Set progress percentage
      const newProgress = Math.round((completedSteps / totalSteps) * 100);
      setProgress(newProgress);
    }
  };

  const fetchProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const { data, error } = await supabase
        .from('users')
        .select('profile_completion_percentage')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching progress:', error);
        return;
      }

      if (data) {
        setProgress(data.profile_completion_percentage);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  // Handle next step
  const handleNextStep = () => {
    if (currentStep < SetupStep.FINAL) {
      setCurrentStep(prevStep => prevStep + 1);
      updateProgress(16.67); // Each step is worth ~16.67% (6 steps total)
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep > SetupStep.WELCOME) {
      setCurrentStep(prevStep => prevStep - 1);
      updateProgress(-16.67); // Reduce progress by ~16.67% per step
    }
  };

  // Update progress in UI and database
  const updateProgress = async (increment: number) => {
    const newProgress = Math.min(100, Math.max(0, progress + increment));
    setProgress(newProgress);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      // Update progress in database
      await supabase
        .from('users')
        .update({ profile_completion_percentage: newProgress })
        .eq('id', user.id);

    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  // Complete setup and navigate to dashboard
  const completeSetup = async () => {
    try {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "User not found",
          variant: "destructive",
        });
        return;
      }

      // Mark onboarding as completed
      const { error } = await supabase
        .from("users")
        .update({
          onboarding_completed: true,
          profile_completion_percentage: 100
        })
        .eq("id", user.id);

      if (error) {
        console.error("Error completing setup:", error);
        toast({
          title: "Error",
          description: "Could not complete your profile setup. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Setup complete!",
        description: "Welcome to your dashboard.",
      });

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (error) {
      console.error("Error completing setup:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Render appropriate content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case SetupStep.WELCOME:
        return (
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold">Welcome to Noche Tonto!</h2>
            <p className="text-gray-300">
              Let's set up your profile so you can get the most out of the platform.
              This will only take a few minutes.
            </p>
            <div className="py-4">
              <img 
                src="https://ehskmwgoablwyqevwegt.supabase.co/storage/v1/object/public/images/Stock_Images/Nightclub5.jpg" 
                alt="Welcome" 
                className="w-64 h-64 object-cover rounded-xl mx-auto opacity-80"
              />
            </div>
          </div>
        );
      
      case SetupStep.BASIC_INFO:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Basic Information</h2>
            <p className="text-gray-300 mb-4">
              Tell us a bit about yourself so others can get to know you better.
            </p>
            
            {/* Basic form components will go here once basic setup is confirmed */}
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                  <h3 className="font-medium mb-2">Personal Details</h3>
                  <p className="text-sm text-gray-400">
                    Your name, location, and contact information
                  </p>
                </div>
                <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                  <h3 className="font-medium mb-2">Bio & Description</h3>
                  <p className="text-sm text-gray-400">
                    Tell others about yourself
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case SetupStep.ID_VERIFICATION:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Verify Your Identity</h2>
            <p className="text-gray-300 mb-4">
              Quick verification to access 18+ venues and build trust in the community.
              This step is optional but recommended.
            </p>
            
            <div className="space-y-4">
              <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                <h3 className="font-medium mb-2 text-green-400">✓ Australian Digital ID (Recommended)</h3>
                <p className="text-sm text-gray-400 mb-3">
                  Fast verification using myGovID or state digital identity services
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>• Instant verification with government records</li>
                  <li>• No document uploads required</li>
                  <li>• Highest security and trust level</li>
                </ul>
              </div>
              
              <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                <h3 className="font-medium mb-2">Document Scan (Fallback)</h3>
                <p className="text-sm text-gray-400 mb-3">
                  Upload ID documents for verification if digital ID is not available
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>• Driver's license, passport, or ID card</li>
                  <li>• Face verification with liveness detection</li>
                  <li>• TEX-powered security</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <p className="text-sm text-blue-300">
                <Shield className="w-4 h-4 inline mr-1" />
                Your privacy is protected. No ID documents are stored on our servers.
              </p>
            </div>
          </div>
        );

      case SetupStep.PROFILE_PIC:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Profile Picture</h2>
            <p className="text-gray-300 mb-4">
              Upload a profile picture to make your profile more recognizable.
            </p>
            
            <div className="flex justify-center py-8">
              <div className="w-32 h-32 rounded-full bg-gray-800 border-2 border-dashed border-gray-600 flex items-center justify-center">
                <span className="text-gray-500">Add Photo</span>
              </div>
            </div>
          </div>
        );
      
      case SetupStep.ROLE_DETAILS:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Role-Specific Details</h2>
            <p className="text-gray-300 mb-4">
              Customize your profile based on how you'll use the platform.
            </p>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                  <h3 className="font-medium mb-2">Role Selection</h3>
                  <p className="text-sm text-gray-400">
                    Choose which roles apply to you
                  </p>
                </div>
                <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                  <h3 className="font-medium mb-2">Preferences</h3>
                  <p className="text-sm text-gray-400">
                    Music, events, and notification settings
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case SetupStep.FINAL:
        return (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold">All Set!</h2>
            <p className="text-gray-300">
              Your profile is ready to go. You can always update these details later in your settings.
            </p>
            <div className="pt-4">
              <Button 
                onClick={completeSetup} 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                disabled={isLoading}
              >
                {isLoading ? "Finalizing..." : "Take Me to Dashboard"}
              </Button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (isLoading && currentStep === SetupStep.WELCOME) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-purple-500 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="mb-8">
            <Progress value={progress} className="h-2 bg-gray-800" />
            <div className="text-right text-sm text-gray-400 mt-1">
              {progress}% Complete
            </div>
          </div>
          
          <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-8 backdrop-blur-sm">
            {renderStepContent()}
            
            <div className="flex justify-between mt-8">
              {currentStep > SetupStep.WELCOME ? (
                <Button 
                  variant="outline" 
                  onClick={handlePrevStep}
                  className="border-gray-700 text-gray-300"
                  disabled={isLoading}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div></div> // Empty div to maintain layout when back button is not shown
              )}
              
              {currentStep < SetupStep.FINAL ? (
                <Button 
                  onClick={handleNextStep}
                  className="bg-gradient-neon bg-200% animate-text-shimmer hover-scale"
                  disabled={isLoading}
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSetupWizard;
